<template>
  <field-frame :field="field" :show-optional="showOptional">
    <div class="formatted-input-container inline-flex flex-wrap items-center gap-1 leading-loose text-sm">
      <template v-for="(item, index) in items" :key="index">
        <!-- 静的テキスト -->
        <span v-if="item.type === 'text'" class="whitespace-nowrap text-q4cl-ink">{{ item.value }}</span>

        <!-- %d 数値入力 (旧版踏襲: type=text + inputmode=numeric + 動的幅) -->
        <input v-else-if="item.type === 'number'"
          v-model="item.value"
          type="text"
          inputmode="numeric"
          :style="{ width: inputWidth(item.value || '') + 'em' }"
          class="field-input !py-1 text-center min-w-[4em]"
          :placeholder="placeholderParts[0] || ''"
          @input="onInput"
          @keydown="blurWithEnterKey"
        />

        <!-- %s テキスト入力 -->
        <input v-else-if="item.type === 'string'"
          v-model="item.value"
          type="text"
          :style="{ width: inputWidth(item.value || placeholderParts[1] || '') + 'em' }"
          class="field-input !py-1 min-w-[8em]"
          :placeholder="placeholderParts[1] || ''"
          @input="onInput"
          @keydown="blurWithEnterKey"
        />

        <!-- %l(...) セレクト -->
        <select v-else-if="item.type === 'select'"
          v-model="item.value"
          class="field-input !py-1 !w-auto"
          @change="onInput"
        >
          <option value="">{{ placeholderParts[2] || $t('選択してください') }}</option>
          <option v-for="opt in item.options" :key="opt" :value="opt">{{ $t(opt) }}</option>
        </select>

        <!-- %r(...) ラジオ -->
        <span v-else-if="item.type === 'radio'" class="inline-flex flex-wrap gap-2">
          <label v-for="opt in item.options" :key="opt"
            class="inline-flex items-center cursor-pointer text-sm">
            <input type="radio"
              :name="`${field.name}_${index}`"
              :value="opt"
              v-model="item.value"
              @change="onInput"
              class="w-4 h-4 mr-1"
            />
            <span>{{ $t(opt) }}</span>
          </label>
        </span>
      </template>
    </div>
  </field-frame>
</template>

<script>
import FieldFrame from '@/components/ui/FieldFrame.vue'
/**
 * 旧版踏襲: Q4CL-client/components/FormattedInput.vue:84-203 のロジックを
 * Composition API 形式で移植 (機能・挙動・出力文字列は同一)。
 *
 *  - format 内で %d, %s, %l(a|b|c), %r(a|b|c) をパースして input/select/radio を散りばめる
 *  - placeholder は '|' 区切りで [0]=%d用, [1]=%s用, [2]=%l/r用 として分配
 *  - 全フィールドが空の場合は '' を emit (required 判定用)、 1 つでも値があれば結合文字列
 *  - 動的幅 (全角 1.6em, 半角 1.0em, 最小 4em)
 *  - format 文字列自体は i18n を通さない (生のテンプレートとしてパース)
 */
export default {
  name: 'FormattedInputField',
  components: {
    'field-frame': FieldFrame
  },
  props: {
    field: { type: Object, required: true },
    showOptional: { type: Boolean, default: false }
  },
  setup(props) {
    const items = Vue.reactive([]);

    const placeholderParts = Vue.computed(() => {
      const ph = props.field.placeholder || '';
      return String(ph).split('|');
    });

    function parseFormat() {
      const out = [];
      const fmt = props.field.format || '';
      let text = '';
      let i = 0;

      while (i < fmt.length) {
        const ch = fmt[i];
        if (ch === '%' && i + 1 < fmt.length) {
          if (text) { out.push({ type: 'text', value: text }); text = ''; }
          const next = fmt[i + 1];

          // %d 数値
          if (next === 'd') { out.push({ type: 'number', value: '' }); i += 2; continue; }
          // %s 文字列
          if (next === 's') { out.push({ type: 'string', value: '' }); i += 2; continue; }
          // %l(...) セレクト
          if (next === 'l') {
            const m = fmt.substring(i).match(/^%l\(([^)]+)\)/);
            if (m) {
              out.push({ type: 'select', value: '', options: m[1].split('|').map((s) => s.trim()) });
              i += m[0].length; continue;
            }
          }
          // %r(...) ラジオ
          if (next === 'r') {
            const m = fmt.substring(i).match(/^%r\(([^)]+)\)/);
            if (m) {
              out.push({ type: 'radio', value: '', options: m[1].split('|').map((s) => s.trim()) });
              i += m[0].length; continue;
            }
          }
        }
        text += ch;
        i++;
      }
      if (text) out.push({ type: 'text', value: text });
      return out;
    }

    function onInput() {
      // 全 token を巡って結合。 1 つでも値があれば結合文字列、 全空なら ''
      let result = '';
      let hasValue = false;
      for (const it of items) {
        if (it.type === 'text') {
          result += it.value;
        } else if (it.value) {
          hasValue = true;
          result += it.value;
        }
        // 未入力の input/select/radio は何も足さない (旧版踏襲)
      }
      props.field.value = hasValue ? result : '';
    }

    function inputWidth(s) {
      // 全角 1.6em / 半角 1.0em、 最小 4em (旧版踏襲)
      let w = 0;
      for (let i = 0; i < s.length; i++) {
        w += (s.charCodeAt(i) > 0xFF) ? 1.6 : 1.0;
      }
      return Math.max(w + 1, 4);
    }

    function blurWithEnterKey(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (e.target && typeof e.target.blur === 'function') e.target.blur();
      }
    }

    Vue.onMounted(() => {
      const parsed = parseFormat();
      items.splice(0, items.length, ...parsed);
      onInput();
    });

    Vue.watch(() => props.field.format, () => {
      const parsed = parseFormat();
      items.splice(0, items.length, ...parsed);
      onInput();
    });

    return { items, placeholderParts, onInput, inputWidth, blurWithEnterKey };
  }
};
</script>
